/**
 * FlowDialogs — shared style objects.
 *
 * Extracted from FlowDialogs.tsx (SaveAs / LoadLibrary / NewMap / EditMapInfo)
 * to keep the dialog components focused on logic. Pure CSSProperties presets
 * on the shared theme. (A few component-specific styles stay co-located with
 * their dialog.)
 */

import React from 'react';
import { color, ui } from '../../components/ui/theme';

// ── Styles ──────────────────────────────────────────────────────────────────

// Shared scrim — kept at zIndex 5000 so these dialogs sit above the Flow
// editor's floating windows (which use high z-indices).
export const backdropStyle: React.CSSProperties = { ...ui.overlay, zIndex: 5000 };

// Shared glass-modal shell + this dialog group's content padding/width.
// Floating glass look (matches the editors' new panels): big rounded corners,
// blurred translucent surface, soft shadow + faint accent ring.
export const dialogStyle: React.CSSProperties = {
  ...ui.dialog,
  width: 380,
  padding: 24,
  gap: 12,
  borderRadius: 20,
  background: 'oklch(0.10 calc(0.018 * var(--tint-c)) var(--tint-h) / 0.86)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.28)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 24px 70px rgba(0, 0, 0, 0.6), 0 0 30px oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.12)',
};

export const titleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  letterSpacing: 0.6,
};

export const subtitleStyle: React.CSSProperties = {
  fontSize: 11,
  color: color.inkDim,
  marginTop: -8,
};

export const codeStyle: React.CSSProperties = {
  fontFamily: "'JetBrains Mono', ui-monospace, Menlo, Consolas, monospace",
  fontSize: 10.5,
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  padding: '1px 5px',
  borderRadius: 4,
};

export const labelStyle: React.CSSProperties = {
  fontSize: 10,
  color: color.inkDim,
  letterSpacing: 0.8,
  marginTop: 4,
};

export const inputStyle: React.CSSProperties = { ...ui.input, padding: '8px 10px', fontSize: 13 };

export const hintStyle: React.CSSProperties = {
  fontSize: 10.5,
  color: '#7A7099',
  lineHeight: 1.4,
};

export const btnRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: 6,
};

export const primaryBtnStyle: React.CSSProperties = {
  padding: '8px 18px',
  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.25), rgba(6, 182, 212, 0.18))',
  border: '1px solid rgba(34, 197, 94, 0.5)',
  borderRadius: 6,
  color: color.ink,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.8,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const secondaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  padding: '8px 14px',
  background: 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.10)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.35)',
  borderRadius: 6,
  color: color.ink,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: 0.6,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginRight: 'auto',
};

export const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  background: 'transparent',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 6,
  color: color.inkDim,
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: 0.6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const listWrapStyle: React.CSSProperties = {
  // ~5 cover rows tall, then scrolls inside this box only.
  maxHeight: 430,
  overflowY: 'auto',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.18)',
  borderRadius: 8,
  background: 'rgba(8, 5, 20, 0.6)',
  padding: 4,
};

export const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
};

export const itemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'stretch',
  gap: 4,
};

export const itemMainStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  padding: '8px 10px',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.5)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 6,
  cursor: 'pointer',
  color: color.ink,
  textAlign: 'left',
  fontFamily: 'inherit',
};

export const itemNameStyle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
};

export const itemMetaStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#7A7099',
};

export const deleteBtnStyle: React.CSSProperties = {
  padding: '0 10px',
  background: 'transparent',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.15)',
  borderRadius: 6,
  color: color.inkDim,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const confirmRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 2,
};

export const confirmDelBtnStyle: React.CSSProperties = {
  padding: '0 10px',
  background: 'rgba(220, 38, 38, 0.18)',
  border: '1px solid rgba(220, 38, 38, 0.5)',
  borderRadius: 6,
  color: '#FCA5A5',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.5,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const confirmCancelBtnStyle: React.CSSProperties = {
  padding: '0 8px',
  background: 'transparent',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  borderRadius: 6,
  color: color.inkDim,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

export const loadingStyle: React.CSSProperties = {
  padding: '40px 12px',
  textAlign: 'center',
  color: '#7A7099',
  fontSize: 12,
};

export const emptyStyle: React.CSSProperties = {
  padding: '40px 12px',
  textAlign: 'center',
  color: '#7A7099',
  fontSize: 12,
};

export const errorStyle: React.CSSProperties = {
  padding: '40px 12px',
  textAlign: 'center',
  color: '#FCA5A5',
  fontSize: 11.5,
  lineHeight: 1.5,
};
